import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

import { AccessGuard } from '@app/access/access-guard';
import { EAccess } from '@app/access/access.model';
import { IUserNotificationRow } from '@app/models/notifications/user-notification.model';
import { AuthenticationService } from '@core/services/auth.service';
import { UserNotificationService } from './user-notification.service';

/**
 * The bell's state: the current user's notifications with an unseen bubble that
 * clears on viewing. Distinct from AlertNotificationService, which tracks OPEN
 * high-risk alerts (banner + menu badge) and only clears on resolution.
 */
@Injectable({ providedIn: 'root' })
export class NotificationFeedService {
  private static readonly DropdownSize = 8;

  public readonly unseenCount$ = new BehaviorSubject<number>(0);
  public readonly recent$ = new BehaviorSubject<IUserNotificationRow[]>([]);
  public readonly available$ = new BehaviorSubject<boolean>(false);

  constructor(
    router: Router,
    private auth: AuthenticationService,
    private userNotificationService: UserNotificationService
  ) {
    this.auth.currentUserObs.subscribe(() => this.refresh());
    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        throttleTime(10000)
      )
      .subscribe(() => this.refresh());
  }

  public refresh(): void {
    const available = !!this.auth.currentUser && AccessGuard.hasAccess(EAccess.UserNotification);
    this.available$.next(available);
    if (!available) {
      this.unseenCount$.next(0);
      this.recent$.next([]);
      return;
    }

    this.userNotificationService
      .getMyNotifications({ page: 1, pageSize: NotificationFeedService.DropdownSize })
      .subscribe({
        next: (res) => {
          this.unseenCount$.next(res?.unseenCount ?? 0);
          this.recent$.next(res?.rows ?? []);
        },
        error: () => {
          // Leave the last known state - a transient failure must not flap the bell.
        }
      });
  }

  /** Called when the dropdown opens: viewing IS dismissing for the bubble; history keeps the rows. */
  public markAllSeen(): void {
    if (this.unseenCount$.value === 0) return;
    this.userNotificationService.markAllSeen().subscribe({
      next: () => this.refresh(),
      error: () => { }
    });
  }
}
